import React, { useState } from 'react';
import Modal from '../common/Modal';
import ModalContent from '../common/ModalContent';
import ModalCloseButton from '../common/ModalCloseButton';
import Button from '../common/Button';

/**
 * Modal for setting late joiner penalty when a player joins after the session has started.
 * Shows suggested penalty based on average games played and allows host to accept, modify, or reject.
 */
const LatePenaltyModal = ({
  player,
  suggestedPenalty,
  averageGames,
  playerGamesPlayed = 0,
  isReturningPlayer = false,
  onConfirm,
  onCancel,
}) => {
  const [penalty, setPenalty] = useState(suggestedPenalty);
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm(penalty);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNoPenalty = async () => {
    setIsLoading(true);
    try {
      await onConfirm(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <Modal isOpen={true} onClose={handleCancel}>
      <ModalContent className="max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-text-primary">
            {isReturningPlayer ? 'Catch-up Penalty' : 'Late Joiner Penalty'}
          </h2>
          <ModalCloseButton onClick={handleCancel} />
        </div>

        <div className="space-y-4">
          <div className="text-text-secondary">
            <p className="mb-3">
              <span className="font-medium text-text-primary">{player?.first_name} {player?.last_name}</span>
              {isReturningPlayer
                ? ' is rejoining the queue after pausing.'
                : ' is joining after the session has started.'}
            </p>

            <div className="bg-info-light border border-border-medium rounded-lg p-3 mb-4">
              <h3 className="font-medium text-text-primary mb-2">Current session state:</h3>
              <ul className="text-sm text-text-secondary space-y-1">
                {isReturningPlayer ? (
                  <li>• Already played: <span className="font-medium text-text-primary">{playerGamesPlayed}</span> game{playerGamesPlayed !== 1 ? 's' : ''}</li>
                ) : null}
                <li>• Average games played: <span className="font-medium text-text-primary">{averageGames.toFixed(1)}</span> games</li>
                <li>• Suggested penalty: <span className="font-medium text-text-primary">{suggestedPenalty}</span> game{suggestedPenalty !== 1 ? 's' : ''}</li>
              </ul>
            </div>

            <div className="bg-surface-subtle border border-border-medium rounded-lg p-3 mb-4">
              <p className="text-sm text-text-secondary">
                {isReturningPlayer
                  ? 'The penalty tops up their effective games count to match the field — only the gap above what they have already played.'
                  : "The penalty is added to their games count for fair matchmaking. This ensures they don't get priority over players who have been playing since the start."}
              </p>
            </div>

            <div className="space-y-3">
              <label htmlFor="penalty-input" className="form-label">
                Penalty (games to add):
              </label>
              <input
                id="penalty-input"
                type="number"
                min="0"
                max="20"
                value={penalty}
                onChange={(e) => setPenalty(Math.max(0, parseInt(e.target.value) || 0))}
                className="w-full form-input focus-ring"
                placeholder="Enter penalty amount"
              />
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              onClick={handleConfirm}
              disabled={isLoading}
              variant="primary"
              className="flex-1"
            >
              {isLoading ? 'Applying...' : `Apply Penalty (${penalty})`}
            </Button>
            
            <Button
              onClick={handleNoPenalty}
              disabled={isLoading}
              variant="secondary"
              className="flex-1"
            >
              {isLoading ? 'Applying...' : 'No Penalty (0)'}
            </Button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default LatePenaltyModal;
